import { isAllowedCountryCode } from '@artem-lex/common-server/countries'
import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ALL_COUNTRIES } from '@/shared/constants/countries'
import {
  useCurrentOrganization,
  useUpdateOrganization,
} from '@/shared/hooks/api/organization'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { PopoverCombobox } from '@/components/ui/popover-combobox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

const MAX_FIELD_LENGTH = 255

function createOrganizationFormSchema(
  translate: (key: string, options?: Record<string, unknown>) => string,
  options: { enforceOnboardingAddressRequired: boolean }
) {
  const streetSchema = options.enforceOnboardingAddressRequired
    ? z
        .string()
        .trim()
        .min(1, translate('settingsOrganization.validation.streetRequired'))
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.streetMax', {
            max: MAX_FIELD_LENGTH,
          })
        )
    : z
        .string()
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.streetMax', {
            max: MAX_FIELD_LENGTH,
          })
        )

  const streetNumberSchema = options.enforceOnboardingAddressRequired
    ? z
        .string()
        .trim()
        .min(
          1,
          translate('settingsOrganization.validation.streetNumberRequired')
        )
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.streetNumberMax', {
            max: MAX_FIELD_LENGTH,
          })
        )
    : z
        .string()
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.streetNumberMax', {
            max: MAX_FIELD_LENGTH,
          })
        )

  const citySchema = options.enforceOnboardingAddressRequired
    ? z
        .string()
        .trim()
        .min(1, translate('settingsOrganization.validation.cityRequired'))
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.cityMax', {
            max: MAX_FIELD_LENGTH,
          })
        )
    : z
        .string()
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.cityMax', {
            max: MAX_FIELD_LENGTH,
          })
        )

  const postCodeSchema = options.enforceOnboardingAddressRequired
    ? z
        .string()
        .trim()
        .min(1, translate('settingsOrganization.validation.postCodeRequired'))
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.postCodeMax', {
            max: MAX_FIELD_LENGTH,
          })
        )
    : z
        .string()
        .max(
          MAX_FIELD_LENGTH,
          translate('settingsOrganization.validation.postCodeMax', {
            max: MAX_FIELD_LENGTH,
          })
        )

  const countrySchema = options.enforceOnboardingAddressRequired
    ? z
        .string()
        .trim()
        .min(1, translate('settingsOrganization.validation.countryRequired'))
        .refine((value) => value.length === 2, {
          message: translate('settingsOrganization.validation.countryCodeLength'),
        })
        .refine(isAllowedCountryCode, {
          message: translate(
            'settingsOrganization.validation.countryCodeUnsupported'
          ),
        })
    : z
        .string()
        .transform((string) => string.trim())
        .refine((value) => value === '' || value.length === 2, {
          message: translate('settingsOrganization.validation.countryCodeLength'),
        })
        .refine((value) => value === '' || isAllowedCountryCode(value), {
          message: translate(
            'settingsOrganization.validation.countryCodeUnsupported'
          ),
        })

  return z.object({
    name: z
      .string()
      .trim()
      .min(1, translate('settingsOrganization.validation.nameRequired'))
      .max(120, translate('settingsOrganization.validation.nameMax')),
    localName: z
      .string()
      .max(
        MAX_FIELD_LENGTH,
        translate('settingsOrganization.validation.localNameMax', {
          max: MAX_FIELD_LENGTH,
        })
      ),
    street: streetSchema,
    street2: z
      .string()
      .max(
        MAX_FIELD_LENGTH,
        translate('settingsOrganization.validation.street2Max', {
          max: MAX_FIELD_LENGTH,
        })
      ),
    streetNumber: streetNumberSchema,
    subDivision: z
      .string()
      .max(
        MAX_FIELD_LENGTH,
        translate('settingsOrganization.validation.subDivisionMax', {
          max: MAX_FIELD_LENGTH,
        })
      ),
    country: countrySchema,
    postCode: postCodeSchema,
    poBox: z
      .string()
      .max(
        MAX_FIELD_LENGTH,
        translate('settingsOrganization.validation.poBoxMax', {
          max: MAX_FIELD_LENGTH,
        })
      ),
    city: citySchema,
    contactName: z
      .string()
      .max(
        MAX_FIELD_LENGTH,
        translate('settingsOrganization.validation.contactNameMax', {
          max: MAX_FIELD_LENGTH,
        })
      ),
    contactPhoneNumber: z
      .string()
      .max(
        MAX_FIELD_LENGTH,
        translate('settingsOrganization.validation.contactPhoneNumberMax', {
          max: MAX_FIELD_LENGTH,
        })
      ),
    contactEmail: z.union([
      z.literal(''),
      z
        .string()
        .email(translate('settingsOrganization.validation.contactEmailInvalid')),
    ]),
  })
}

type OrganizationFormValues = z.infer<
  ReturnType<typeof createOrganizationFormSchema>
>

function toNullable(value: string): string | null {
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

type OrganizationFormInnerProps = {
  enforceOnboardingAddressRequired: boolean
}

function OrganizationFormInner({
  enforceOnboardingAddressRequired,
}: OrganizationFormInnerProps) {
  const { t } = useTranslation(['common', 'countries'])
  const { data: organization, isLoading: isOrganizationLoading } =
    useCurrentOrganization()
  const { mutateAsync: updateOrganization, isPending: isUpdating } =
    useUpdateOrganization()
  const organizationFormSchema = useMemo(
    () =>
      createOrganizationFormSchema(t, {
        enforceOnboardingAddressRequired,
      }),
    [t, enforceOnboardingAddressRequired]
  )

  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: {
      name: '',
      localName: '',
      street: '',
      street2: '',
      streetNumber: '',
      subDivision: '',
      country: '',
      postCode: '',
      poBox: '',
      city: '',
      contactName: '',
      contactPhoneNumber: '',
      contactEmail: '',
    },
  })

  useEffect(() => {
    if (!organization) {
      return
    }
    form.reset({
      name: organization.name,
      localName: organization.localName ?? '',
      street: organization.street ?? '',
      street2: organization.street2 ?? '',
      streetNumber: organization.streetNumber ?? '',
      subDivision: organization.subDivision ?? '',
      country: (organization.country ?? '').toLowerCase(),
      postCode: organization.postCode ?? '',
      poBox: organization.poBox ?? '',
      city: organization.city ?? '',
      contactName: organization.contactName ?? '',
      contactPhoneNumber: organization.contactPhoneNumber ?? '',
      contactEmail: organization.contactEmail ?? '',
    })
  }, [organization, form])

  async function onSubmit(data: OrganizationFormValues) {
    if (!organization) {
      return
    }

    await updateOrganization({
      organizationId: organization.id,
      payload: {
        name: data.name.trim(),
        localName: toNullable(data.localName),
        street: toNullable(data.street),
        street2: toNullable(data.street2),
        streetNumber: toNullable(data.streetNumber),
        subDivision: toNullable(data.subDivision),
        country: toNullable(data.country),
        postCode: toNullable(data.postCode),
        poBox: toNullable(data.poBox),
        city: toNullable(data.city),
        contactName: toNullable(data.contactName),
        contactPhoneNumber: toNullable(data.contactPhoneNumber),
        contactEmail: toNullable(data.contactEmail),
      },
    })

    toast.success(t('settingsOrganization.updatedSuccess'))
  }

  const isDisabled = isOrganizationLoading || isUpdating || !organization

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.nameLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.namePlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='localName'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.localNameLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.localNamePlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='street'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.streetLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.streetPlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='street2'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.street2Label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.street2Placeholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='streetNumber'
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('settingsOrganization.streetNumberLabel')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.streetNumberPlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='subDivision'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.subDivisionLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.subDivisionPlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='city'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.cityLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.cityPlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='country'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.countryLabel')}</FormLabel>
              <FormControl>
                <PopoverCombobox
                  id='organization-country'
                  items={ALL_COUNTRIES}
                  value={field.value ? field.value : null}
                  onValueChange={(code) => field.onChange(code ?? '')}
                  getItemLabel={(code) => t(code, { ns: 'countries' }) || code}
                  getItemKey={(code) => code}
                  placeholder={t('settingsOrganization.countryPlaceholder')}
                  searchPlaceholder={t(
                    'settingsOrganization.countrySearchPlaceholder'
                  )}
                  emptyText={t('settingsOrganization.countryEmpty')}
                  disabled={isDisabled}
                  showClear={!enforceOnboardingAddressRequired}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='postCode'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.postCodeLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.postCodePlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='poBox'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.poBoxLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.poBoxPlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='contactName'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.contactNameLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.contactNamePlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='contactPhoneNumber'
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('settingsOrganization.contactPhoneNumberLabel')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t(
                    'settingsOrganization.contactPhoneNumberPlaceholder'
                  )}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='contactEmail'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsOrganization.contactEmailLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsOrganization.contactEmailPlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormDescription>
                {t('settingsOrganization.contactEmailDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type='submit' disabled={isDisabled}>
          {t('settingsOrganization.updateButton')}
        </Button>
      </form>
    </Form>
  )
}

export function OrganizationForm() {
  const { data: organization } = useCurrentOrganization()
  const enforceOnboardingAddressRequired =
    organization?.onboardingCompletedAt != null

  return (
    <OrganizationFormInner
      key={`${organization?.id ?? 'loading'}-${
        enforceOnboardingAddressRequired ? 'onboarded' : 'pending'
      }`}
      enforceOnboardingAddressRequired={enforceOnboardingAddressRequired}
    />
  )
}
